import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMic } from 'react-icons/fi';
import './HeroSection.css';
import { MyContext } from '../../../../Context/MyContext';
import PreferenceModal from './PreferenceModal';
import { saveRecentSearch, buildSearchLabel } from '../../../../utils/userActivity';
import AppIcon from '../../../../Components/AppIcon';
import VoiceSearchModal from '../../../../Components/VoiceSearchModal';
import SearchSuggest from '../../../../Components/SearchSuggest';

const HeroSection = () => {
  const { heroData, selectedCity, preferencesComplete, user } = useContext(MyContext);
  const navigate = useNavigate();
  const { bg_images = [], search_tabs = [], quick_actions = [], free_banner, title, subtitle, search_placeholder } = heroData || {};

  const [bgIndex,    setBgIndex]    = useState(0);
  const [activeTab,  setActiveTab]  = useState(search_tabs[0].id);
  const [searchVal,  setSearchVal]  = useState('');
  const [bannerIdx,  setBannerIdx]  = useState(0);
  const [bannerFade, setBannerFade] = useState(true);
  const [showPreferenceModal, setShowPreferenceModal] = useState(false);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);

  useEffect(() => {
    setVoiceSupported(
      typeof window !== 'undefined'
      && window.isSecureContext
      && !!(window.SpeechRecognition || window.webkitSpeechRecognition),
    );
  }, []);

  const runSearch = useCallback(async (query) => {
    const trimmed = String(query || '').trim();
    if (!trimmed) return;
    const listingType = activeTab === 'rent' ? 'rent' : 'sale';
    const url = `/properties?type=${listingType}&q=${encodeURIComponent(trimmed)}`;
    const label = buildSearchLabel({ search: trimmed, listing_type: listingType });
    await saveRecentSearch({ label, url }, user);
    window.dispatchEvent(new Event('recent-search-updated'));
    navigate(url);
  }, [activeTab, navigate, user]);

  const handleVoiceResult = (text) => {
    setSearchVal(text);
  };

  // Background image cycle
  useEffect(() => {
    if (!bg_images?.length || bg_images.length <= 1) return;
    const t = setInterval(() => {
      setBgIndex(i => (i + 1) % bg_images.length);
    }, 5000);
    return () => clearInterval(t);
  }, [bg_images.length]);

  // Free banner text cycle with fade
  useEffect(() => {
    const t = setInterval(() => {
      setBannerFade(false);
      setTimeout(() => {
        setBannerIdx(i => (i + 1) % free_banner.lines.length);
        setBannerFade(true);
      }, 350);
    }, 3000);
    return () => clearInterval(t);
  }, [free_banner.lines.length]);

  const handleSearch = async () => {
    await runSearch(searchVal);
  };

  return (
    <section className="hero">
      {/* ── Animated background ── */}
      <div className="hero-bg-wrap">
        {bg_images.map((img, i) => (
          <div
            key={i}
            className={`hero-bg-slide ${i === bgIndex ? 'active' : ''}`}
            style={{ backgroundImage: `url(${img})` }}
          />
        ))}
        <div className="hero-bg-dark" />
      </div>

      {/* ── Content ── */}
      <div className="hero-content">

        {/* Free animated banner */}
        <div className="hero-free-wrap">
          <span className="free-pill">FREE</span>
          <span className={`free-text ${bannerFade ? 'fade-in' : 'fade-out'}`}>
            {free_banner.lines[bannerIdx]}
          </span>
        </div>

        <h1 className="hero-title">
          {(title || 'Turning Your Property Dreams\nInto Reality.').split('\n').map((line, i) => (
            <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
          ))}
        </h1>
        <p className="hero-sub">{subtitle || 'Plots, Villas, Apartments, And Rental Properties With Trusted Listings And Verified Sellers & Documentation.'}</p>

        {/* ── Search box ── */}
        <div className="hero-search-wrap">

          <div className="search-tabs-row">
            {/* 1. Rent / Sale tabs */}
            <div className="search-tabs">
              {search_tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`search-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <label className="search-tab-select-wrap">
              <span className="search-tab-select-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                  <path d="M4 7h16" />
                  <path d="M7 12h10" />
                  <path d="M10 17h4" />
                </svg>
              </span>
              <select
                className="search-tab-select"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                aria-label="Select listing type"
              >
                {search_tabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>{tab.label}</option>
                ))}
              </select>
              <span className="search-tab-select-arrow" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </label>

            {/* 2. Preference selector */}
            <div className="hero-city-selector">
              <button
                type="button"
                className="hero-city-tab-btn"
                onClick={() => setShowPreferenceModal(true)}
              >
                <span className="hero-city-tab-icon">
                  <svg
                    className={!preferencesComplete ? 'blinking-pin' : ''}
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </span>
                <span className="hero-city-tab-label">
                  {preferencesComplete && selectedCity
                    ? selectedCity.city_name
                    : 'Let Us Know Your Preference'}
                </span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"
                  className="hero-city-tab-arrow">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>
          </div>

          {/* 3. Search box */}
          <div className="search-box">
            <svg className="search-ico" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" width="18" height="18">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <SearchSuggest
              value={searchVal}
              onChange={setSearchVal}
              onSubmit={(q, item) => {
                const companyId = item?.type === 'company' ? Number(item.id || item.company_id) : null;
                if (Number.isFinite(companyId) && companyId > 0) {
                  navigate(`/company/${companyId}`);
                  return;
                }
                runSearch(q);
              }}
              placeholder={search_placeholder || 'Search by city, location, company, type, or #property no…'}
              inputClassName="search-input"
              showClear={false}
              className="hero-search-suggest"
              attached
            />
            {voiceSupported && (
              <button
                type="button"
                className="search-voice-btn"
                onClick={() => setVoiceModalOpen(true)}
                title="Voice search"
                aria-label="Open voice search"
              >
                <FiMic />
              </button>
            )}
            <button className="search-btn" onClick={handleSearch}>Search</button>
          </div>

        </div>


        {/* ── Quick action cards ── */}
        <div className="hero-actions">
          {quick_actions.map(action => (
            <button
              key={action.id}
              className={`action-card ${action.is_free ? 'action-card-free' : ''}`}
              onClick={() => navigate(action.url)}
              style={{ '--accent': action.color }}
            >
              <span className="ac-icon"><AppIcon name={action.icon} size={22} color="#ffffff" /></span>
              <span className="ac-label">
                {action.label}
                {action.is_free && <span className="ac-free-badge">FREE</span>}
              </span>
              <span className="ac-desc">{action.description}</span>
            </button>
          ))}
        </div>
      </div>
      <PreferenceModal show={showPreferenceModal} onClose={() => setShowPreferenceModal(false)} />
      <VoiceSearchModal
        open={voiceModalOpen}
        onClose={() => setVoiceModalOpen(false)}
        onResult={handleVoiceResult}
      />
    </section>
  );
};

export default HeroSection;